/**
 * Full-screen privacy gate — LEAD-SAFE.
 *
 * Live traffic: never shows (VPN/proxy/Tor flags are analytics-only).
 * geo_sim anonymous|hosting: overlay for QA only, and only on non-conversion paths.
 *
 * Bypass (no overlay — CTAs stay visible on desktop + mobile):
 * - Clerk super_admin (wait for Clerk load — avoid flash over DualCTA)
 * - geo_sim + simulate_vpn=clean (session or URL, even before snapshot)
 * - IP allowlist (e.g. 190.229.184.199)
 * - Home DualCTA `/` + all invite/checkout/thank-you conversion paths
 * - Any live-mode snapshot (fail-open even if server wrongly sends blocked)
 *
 * Note: DualCTA CTAs are NOT md:hidden; if QA can't see them, this gate was
 * likely covering the phone-frame (z-9990 > CTA z-110).
 */

import { ShieldAlert } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { useVisitorPrivacyGate } from "@/hooks/useVisitorPrivacyGate";
import { isGeoSimUrlActive, readGeoSimSession } from "@/lib/geoSim/session";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";

function isGeoSimCleanBypass(): boolean {
  if (typeof window === "undefined") return false;
  const session = readGeoSimSession();
  if (session && (session.simulateVpn || "clean") === "clean") return true;
  if (!isGeoSimUrlActive()) return false;
  try {
    const mode =
      new URLSearchParams(window.location.search).get("simulate_vpn") || "clean";
    return mode === "clean";
  } catch {
    return false;
  }
}

/**
 * Outer shell: without ClerkProvider (missing publishable key) useUser throws
 * and ErrorBoundary kills the whole SPA — including /invitacion-exclusiva.
 */
export function VisitorPrivacyGate() {
  if (!getClerkPublishableKey()) return null;
  return <VisitorPrivacyGateWithClerk />;
}

function VisitorPrivacyGateWithClerk() {
  const { isLoaded: clerkLoaded } = useUser();
  const { ready, blocked, blockReason, mode, allowlisted } =
    useVisitorPrivacyGate();

  // Never cover conversion CTAs while Clerk/session resolve (QA desktop + mobile)
  if (!clerkLoaded || !ready) return null;
  if (isGeoSimCleanBypass()) return null;
  if (!blocked) return null;

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/90 px-6 text-center text-white"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="privacy-gate-title"
    >
      <div className="max-w-md space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20">
          <ShieldAlert className="h-7 w-7 text-amber-300" />
        </div>
        <h1 id="privacy-gate-title" className="text-xl font-semibold">
          Red no disponible
        </h1>
        <p className="text-sm leading-relaxed text-white/70">
          Modo de prueba de red anónima (solo QA geo_sim). En tráfico real la
          VPN y el acceso al sitio no se bloquean para no perder leads.
        </p>
        {process.env.NODE_ENV !== "production" || mode !== "live" ? (
          <p className="font-mono text-[10px] text-white/40">
            reason={blockReason || "—"} mode={mode}
            {allowlisted ? " allowlisted" : ""}
          </p>
        ) : null}
        <a
          href="/"
          className="inline-flex rounded-xl bg-white px-4 py-2 text-sm font-medium text-black hover:bg-white/90"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
}
